import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";

const RANGES = [7, 30, 90];

const TIER_HEX = {
  Bronze: "#b45309",
  Silver: "#cbd5e1",
  Gold: "#eab308",
  Platinum: "#67e8f9",
  Diamond: "#38bdf8",
  Master: "#e879f9",
  Elite: "#fb7185",
};

function Sparkline({ values, color = "#34d399", height = 48 }) {
  if (!values || values.length === 0) return null;
  const w = 240;
  const h = height;
  const pad = 3;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const stepX = (w - pad * 2) / Math.max(1, values.length - 1);
  const pts = values.map((v, i) => {
    const x = pad + i * stepX;
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return [x, y];
  });
  const line = pts
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(" ");
  const area = `${line} L${pts[pts.length - 1][0].toFixed(1)},${h} L${pts[0][0].toFixed(1)},${h} Z`;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="w-full"
      style={{ height }}
    >
      <path d={area} fill={color} opacity="0.12" />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

// data: [{ label, value, color?, tick? }]
function BarChart({ data, height = 140, defaultColor = "#34d399" }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div>
      <div className="flex items-end gap-[2px]" style={{ height }}>
        {data.map((d, i) => (
          <div
            key={i}
            title={`${d.label}: ${d.value}`}
            className="flex-1 min-w-0 rounded-t-sm hover:opacity-80 transition"
            style={{
              height: `${d.value > 0 ? Math.max(3, (d.value / max) * 100) : 0}%`,
              backgroundColor: d.color || defaultColor,
            }}
          />
        ))}
      </div>
      <div className="flex gap-[2px] mt-1">
        {data.map((d, i) => (
          <div
            key={i}
            className="flex-1 min-w-0 text-center text-[8px] text-zinc-600 truncate"
          >
            {d.tick || ""}
          </div>
        ))}
      </div>
    </div>
  );
}

function StatTable({ title, rows }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="rounded-2xl bg-zinc-900/50 border border-zinc-800 p-5">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
        {title}
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-zinc-600">No data yet</div>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-3 text-xs">
              <span className="w-4 text-zinc-600 tabular-nums text-right">
                {i + 1}
              </span>
              <span className="flex-1 min-w-0 truncate text-zinc-200">
                {r.label}
                {r.sub && <span className="text-zinc-600 ml-1.5">{r.sub}</span>}
              </span>
              <div className="w-20 md:w-24 h-1.5 rounded-full bg-zinc-800 overflow-hidden shrink-0">
                <div
                  className="h-full bg-emerald-500/60"
                  style={{ width: `${(r.value / max) * 100}%` }}
                />
              </div>
              <span className="w-8 text-right tabular-nums text-zinc-400 shrink-0">
                {r.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Panel({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl bg-zinc-900/50 border border-zinc-800 p-5">
      <div className="flex items-baseline justify-between mb-4">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">
          {title}
        </div>
        {subtitle && (
          <div className="text-[10px] text-zinc-600">{subtitle}</div>
        )}
      </div>
      {children}
    </div>
  );
}

export default function AdminPage() {
  const { user, isAdmin, loading } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(true);
  const [gp, setGp] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    setBusy(true);
    setErr(null);
    supabase.rpc("admin_metrics", { days }).then(({ data, error }) => {
      if (error) setErr(error.message);
      else setData(data);
      setBusy(false);
    });
  }, [isAdmin, days]);

  useEffect(() => {
    if (!isAdmin) return;
    supabase.rpc("admin_gameplay_stats").then(({ data, error }) => {
      if (!error) setGp(data);
    });
  }, [isAdmin]);

  if (loading || (user && isAdmin === null)) {
    return (
      <div className="h-screen grid place-items-center text-sm text-zinc-500">
        Loading…
      </div>
    );
  }
  if (!user || !isAdmin) return <Navigate to="/" replace />;

  const daily = data?.daily || [];
  const baseline = data?.baseline || { users: 0, standard: 0, ranked: 0 };
  const cumulative = (key) => {
    let acc = baseline[key] || 0;
    return daily.map((d) => (acc += d[key] || 0));
  };
  const sumNew = (key) => daily.reduce((s, d) => s + (d[key] || 0), 0);

  const cards = [
    { key: "users", label: "Signed-up users", color: "#34d399" },
    { key: "standard", label: "Standard games", color: "#60a5fa" },
    { key: "ranked", label: "Ranked games", color: "#f472b6" },
  ];

  const winsData = (gp?.standard_wins || []).map((d) => ({
    label: `${d.wins} wins`,
    value: d.count,
    tick: [0, 10, 20, 30, 38].includes(d.wins) ? String(d.wins) : "",
  }));
  const tierData = (gp?.ranked_tiers || []).map((d) => ({
    label: d.name,
    value: d.count,
    color: TIER_HEX[d.name] || "#34d399",
    tick: d.name,
  }));
  // Re-enable together with the "Activity by hour" panel below.
  // const hourData = (gp?.by_hour || []).map((d) => ({
  //   label: `${d.hour}:00 UTC`,
  //   value: d.count,
  //   color: "#60a5fa",
  //   tick: d.hour % 6 === 0 ? String(d.hour) : "",
  // }));

  return (
    <div className="h-screen flex flex-col">
      <Header />
      <main className="flex-1 overflow-y-auto scrollbar-fancy">
        <div className="w-full max-w-5xl mx-auto px-5 py-8 md:py-12">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-zinc-100">Admin</h1>
            <div className="flex items-center gap-1.5">
              {RANGES.map((r) => (
                <button
                  key={r}
                  onClick={() => setDays(r)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition ${
                    days === r
                      ? "bg-emerald-500/15 border border-emerald-500/40 text-emerald-300"
                      : "bg-zinc-900/40 border border-zinc-800 text-zinc-400 hover:border-zinc-700"
                  }`}
                >
                  {r}d
                </button>
              ))}
            </div>
          </div>

          {err && (
            <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 mb-6">
              {err}
            </div>
          )}
          {busy && !data && (
            <div className="text-sm text-zinc-500">Loading metrics…</div>
          )}

          {data && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {cards.map((c) => {
                const series = cumulative(c.key);
                const total = data.totals[c.key] ?? 0;
                const added = sumNew(c.key);
                return (
                  <div
                    key={c.key}
                    className="rounded-2xl bg-zinc-900/50 border border-zinc-800 p-5"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                      {c.label}
                    </div>
                    <div className="mt-2 flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-zinc-100 tabular-nums">
                        {total.toLocaleString()}
                      </span>
                      <span className="text-xs font-semibold text-emerald-400 tabular-nums">
                        +{added} / {days}d
                      </span>
                    </div>
                    <div className="mt-4">
                      <Sparkline
                        values={series.length ? series : [baseline[c.key] || 0]}
                        color={c.color}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Gameplay analytics */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Panel title="Standard result distribution" subtitle="wins per run">
              {winsData.length ? (
                <BarChart data={winsData} defaultColor="#60a5fa" />
              ) : (
                <div className="text-xs text-zinc-600">No data yet</div>
              )}
            </Panel>

            <Panel title="Ranked rating distribution" subtitle="by tier">
              {tierData.length ? (
                <BarChart data={tierData} />
              ) : (
                <div className="text-xs text-zinc-600">No data yet</div>
              )}
            </Panel>

            {/* <Panel
              title="Activity by hour"
              subtitle="UTC · runs + ranked games"
            >
              {hourData.length ? (
                <BarChart data={hourData} defaultColor="#60a5fa" />
              ) : (
                <div className="text-xs text-zinc-600">No data yet</div>
              )}
            </Panel> */}

            <StatTable
              title="Most-picked clubs"
              rows={(gp?.top_clubs || []).map((c) => ({
                label: c.club,
                value: c.picks,
              }))}
            />

            <StatTable
              title="Most-picked players"
              rows={(gp?.top_players || []).map((p) => ({
                label: p.name,
                sub: p.nation,
                value: p.picks,
              }))}
            />
          </div>
        </div>
      </main>
    </div>
  );
}
